<template>
    <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="max-w-md mx-auto mt-10">
      <label class="block text-sm font-medium text-gray-700 mb-2">{{ label }}</label>

      <div
        class="border-2 border-dashed rounded-2xl p-4 relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
        :class="{
          'border-green-400 bg-green-50': accepted,
          'border-red-400 bg-red-50': errors.length > 0,
          'hover:border-gray-400': !errors.length > 0 && !accepted
        }"
        role="button"
        tabindex="0"
        @click="openFileDialog"
        @keydown.enter.prevent="openFileDialog"
        @keydown.space.prevent="openFileDialog"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        aria-describedby="filepicker-desc"
      >
        <input
          ref="input"
          type="file"
          class="sr-only"
          :accept="acceptAttr"
          @change="onInputChange"
          :multiple="multiple"
          aria-hidden="true"
        />

        <div class="flex items-center gap-3">
          <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 3v12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 7l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 21H3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>

          <div>
            <p id="filepicker-desc" class="text-sm text-gray-700">Trascina qui i file XML oppure fai click per selezionarli.</p>
            <p class="text-xs text-gray-500 mt-1">Accetta solo file XML (estensione <code class="bg-gray-100 px-1 rounded">.xml</code>)</p>
          </div>
        </div>

        <div v-if="files.length" class="mt-3 space-y-2">
          <div v-for="f in files" :key="f.name" class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-gray-500" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4 7v8a2 2 0 0 0 2 2h8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 3v6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div>
                <div class="text-sm font-medium text-gray-800">{{ f.name }}</div>
                <div class="text-xs text-gray-500">{{ prettySize(f.size) }}</div>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <button type="button" class="text-sm px-3 py-1 rounded-md border" @click.stop="removeFile(f)">Rimuovi</button>
            </div>
          </div>

          <div class="flex justify-end">
            <button type="button" class="text-sm px-3 py-1 rounded-md bg-blue-600 text-white" @click.stop="emitFiles">Carica tutti</button>
          </div>
        </div>

        <p v-if="isLoading">Elaborazione in corso...</p>
        <div v-if="errors.length > 0" class="mt-3 text-sm text-red-700">
          <p><b>Errori:</b></p>
          <p v-for="error in errors">{{ error }}</p>
        </div>
        <p v-else-if="accepted" class="mt-3 text-sm text-green-700">File validi.</p>
      </div>
    </div>
  </div>
</template>

<script>

import { Link, useForm, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Datepicker from 'vue3-datepicker';
import { format } from 'date-fns';
import axios from 'axios';

export default {
  name: 'XmlFilePicker',
  props: {
    label: { type: String, default: 'Seleziona file XML' },
    multiple: { type: Boolean, default: true },
    maxSize: { type: Number, default: 5 * 1024 * 1024 }, // 5 MB
  },
  components: {
    Link,
    BreadCrumbs,
    Datepicker
  },
  data() {
    return {
      input: null,
      files: [],
      errors: [],
      accepted: false,
      acceptAttr: '.xml',
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.ricezione-veneta'),
          href: route('modules.home', 'ricezione-veneta'),},
        { name: this.$t('modules.ricven.caricaxml'),
          href: route('modules.ricven.caricaxml'), }
      ],
      readfileramaining: 0,
      packets: [],
      isLoading: false
    }
  },
  methods: {
    openFileDialog() {
      this.$refs.input && this.$refs.input.click()
    },
    onInputChange(e) {
      const newFiles = e.target.files
      this.handleFiles(newFiles)
      e.target.value = ''
    },
    onDrop(e) {
      const dropped = e.dataTransfer.files
      this.onDragLeave()
      this.handleFiles(dropped)
    },
    onDragEnter() {},
    onDragOver() {},
    onDragLeave() {},
    handleFiles(fileList) {
      this.errors.splice(0, this.errors.length);
      this.accepted = false
      if (!fileList || fileList.length === 0) return

      const validFiles = []

      for (const file of fileList) {
        const name = file.name || ''
        const isXmlExt = name.toLowerCase().endsWith('.xml')

        if (!isXmlExt) {
          this.errors.push(`Il file ${name} non ha estensione xml.`);
          continue
        }

        if (file.size > this.maxSize) {
          this.errors.push(`Il file ${name} è troppo grande. Limite: ${this.prettySize(this.maxSize)}.`)
          continue
        }

        if (this.files.find(x => x.name == file.name && x.size == file.size)){
          this.errors.push(`Il file ${name} è già stato inserito.`)
          continue
        }

        validFiles.push(file)
      }

      if (validFiles.length) {
        this.files = [...this.files, ...validFiles]
      }
    },
    removeFile(f) {
      this.files = this.files.filter(x => x !== f)
      if (!this.files.length) this.accepted = false
    },
    emitFiles() {
      let _this = this;
      let packets = [];

      _this.readfileramaining = _this.files.length;

      for (const file of this.files) {
        let reader = new FileReader();

        reader.addEventListener("loadend", function (evt) { 
          packets.push( { name: file.name , data: new Uint8Array(evt.currentTarget.result).toBase64()}); 
          _this.readfileramaining--;
          if (_this.readfileramaining == 0){
            _this.sendXML(packets);
          }
        });
        reader.readAsArrayBuffer(file)
      }
    },
    prettySize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let size = bytes
      while (size >= 1024 && i < units.length - 1) {
        size = size / 1024
        i++
      }
      return `${Math.round(size * 10) / 10} ${units[i]}`
    },
    sendXML(packets) {
      let _this = this;
      this.isLoading = true;
      axios.post(route('modules.ricven.salvaXML'), { files: packets }).
      then((response) => {
        this.isLoading = false;
        if (response.data.success)
        {
          _this.errors.splice(0, this.errors.length);
          _this.accepted = true;
          _this.files = [];
        }
        else
        {
          if (response.data.errors)
          {
            let keep = [];
            for (var i = 0; i < _this.files.length; i++)
            {
              if (response.data.errors[i] != "")
              {
                keep.push(_this.files[i]);
              }
            }
            _this.files = keep;
            _this.errors = response.data.errors;
          }
          else _this.errors = [response.data];
        }
      });
    }
  }
}
</script>
